import { SerializationException } from '../../exceptions/serialization-exception';

export class VersionRange {
  private static readonly _versionRangeMatcher: RegExp = /(\d+):(\d+)/;

  private constructor(
      public readonly from: number,
      public readonly to: number,
  ) {}

  static between(from: number, to: number): VersionRange {
    return new VersionRange(from, to);
  }

  static exactly(version: number): VersionRange {
    return new VersionRange(version, version);
  }

  contains(version: number): boolean {
    return version >= this.from && version <= this.to;
  }

  isLessThan(version: number): boolean {
    return this.to < version;
  }

  isGreaterThan(version: number): boolean {
    return this.from > version;
  }

  isExact(): boolean {
    return this.from === this.to;
  }

  static mustDecode(value: unknown): VersionRange {
    if(typeof value === 'number') {
      return VersionRange.exactly(value);
    }

    const valueAsStr: string = (value as object).toString();

    const match = valueAsStr.match(VersionRange._versionRangeMatcher);
    if(match) {
      return VersionRange.between(
          parseInt(match[1]),
          parseInt(match[2]),
      );
    }

    throw new SerializationException(`Invalid version range format: ${value}`);
  }

  static decode(value: unknown): VersionRange | null {
    try {
      return VersionRange.mustDecode(value);
    }
    catch{
      return null;
    }
  }

  encode(): string | number {
    return VersionRange._encode(this.from, this.to);
  }

  toString(): string {
    return this.from === this.to ? `${this.from}` : `${this.from}:${this.to}`;
  }

  static encodedBetween(from: number, to: number): string | number {
    return VersionRange._encode(from, to);
  }

  static encodedExactly(version: number): string | number {
    return VersionRange._encode(version, version);
  }

  static _encode(from: number, to: number): string | number {
    if(from === to) {
      return from; //  Returned as [int] for backwards compatibility with older app versions.
    }
    else {
      return `${from}:${to}`;
    }
  }
}
